import { useCallback, useMemo, useState } from 'react';
import type { ReportEvent } from '@/models/reportEvent';
import type { Report } from '@/models/report';
import { mapReportEvents } from '../utils/mapReportEvents';
import { groupTimelineEvents } from '../utils/groupTimelineEvents';
import { synthesizeSignedEvents } from '../utils/synthesizeSignedEvents';

/**
 * `reports` is the study's reports; it lets the timeline backfill a SIGNED
 * entry for FINAL reports whose audit log is missing one (see
 * synthesizeSignedEvents). Defaults to empty for callers without it.
 */
export function useReportTimeline(
  reportEvents: ReportEvent[],
  reports: Report[] = []
) {
  const events = useMemo(
    () => mapReportEvents(synthesizeSignedEvents(reportEvents, reports)),
    [reportEvents, reports]
  );

  const entries = useMemo(() => groupTimelineEvents(events), [events]);

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(
    new Set()
  );

  const selectedEvent = useMemo(
    () => events.find((e) => e.id === selectedEventId) ?? null,
    [events, selectedEventId]
  );

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  return {
    entries,
    events,
    selectedEventId,
    selectEvent: setSelectedEventId,
    selectedEvent,
    expandedGroupIds,
    toggleGroup,
  } as const;
}
